import styled, { css } from "styled-components";
import { FC, useRef } from "react";
import { motion } from "framer-motion";
import { useDispatch } from "react-redux";

import Text from "@/components/Text";
import Button, { Spinner } from "./Button";
import useOnClickOutside from "@/hooks/useOnClickOutside";
import { Dispatch } from "@/utils/store";
import { hexToRgba } from "@/utils/theme";
import { ViewGuideResource } from "@/types/resources";

const GuideModal = styled(motion.div)<{
  $big?: boolean;
}>`
  padding: 16px 16px 24px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  box-shadow: 0px 8px 30px 1px rgba(33, 29, 30, 0.08),
    0px 14px 64px -18px rgba(33, 29, 30, 0.08);
  border-radius: 4px;
  position: absolute;
  bottom: 96px;
  right: 24px;
  width: 344px;
  z-index: 2;
  ${({ $big }) =>
    $big &&
    css`
      width: 488px;
      padding: 24px;
      position: relative;
      bottom: initial;
      right: initial;
    `}
`;

const Title = styled(Text)`
  text-align: center;
  margin-bottom: 12px;
  margin-top: 24px;
`;

const Description = styled(Text)`
  text-align: center;
  padding: 0 16px;
`;

const Buttons = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 16px;
  margin-top: 24px;
`;

const StyledButton = styled(Button)`
  font-size: 14px;
  height: 38px;
  padding: 0;
  border-color: ${({ theme }) => theme.colors.lightGray};
  svg {
    margin-right: 8px;
  }
`;

const LinkToLoom = styled.a`
  ${({ theme }) => css`
    font-family: ${theme.typography.button.fontFamily};
  `}
  font-weight: 700;
  font-size: 14px;
  height: 38px;
  padding: 0;
  justify-content: center;
  transition: 0.2s ease-out all;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  border-radius: 4px;
  background: transparent;
  border: 0;
  background-color: ${({ theme }) => hexToRgba(theme.colors.purpleLight, 0.15)};
  color: ${({ theme }) => theme.colors.purple};
  &:hover,
  &:active {
    background-color: ${({ theme }) =>
      hexToRgba(theme.colors.purpleLight, 0.25)};
  }
  &:disabled {
    color: ${({ theme }) => theme.colors.gray};
    background: ${({ theme }) => theme.colors.grayscaleLight};
  }
  svg {
    margin-right: 8px;
  }
`;

const ThumbnailHolder = styled.div`
  position: relative;
`;

const Iframe = styled.iframe<{
  $big?: boolean;
}>`
  width: 100%;
  height: 176px;
  position: relative;
  z-index: 1;
  ${({ $big }) =>
    $big &&
    css`
      height: 247px;
    `}
`;

const IframeHolder = styled.div`
  position: relative;
`;

const StyledSpinner = styled(Spinner)`
  width: 30px;
  height: 30px;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  margin: auto;
`;

interface Props {
  isBig?: boolean;
  onClose: () => void;
  currentPath: string;
  currentGuide: ViewGuideResource;
  generalLink?: string;
}

const VideoGuide: FC<Props> = ({
  isBig = false,
  onClose,
  currentPath,
  currentGuide,
  generalLink,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch<Dispatch>();

  const onGuideClose = (pathKey: string) => {
    dispatch.viewGuides.setViewGuides({
      pathKey,
    });
    onClose();
  };

  useOnClickOutside(ref, () => onGuideClose(currentPath));
  return (
    <GuideModal $big={isBig} ref={ref}>
      <ThumbnailHolder>
        <IframeHolder>
          <Iframe
            $big={isBig}
            src={currentGuide.link!.replace("share", "embed")}
            allowFullScreen
          ></Iframe>
          <StyledSpinner />
        </IframeHolder>
      </ThumbnailHolder>
      {currentGuide.title && (
        <Title $type={isBig ? "heading2" : "button"}>
          {currentGuide.title}
        </Title>
      )}
      {currentGuide.description && (
        <Description>{currentGuide.description}</Description>
      )}
      <Buttons>
        <StyledButton
          color="secondary"
          onClick={() => onGuideClose(currentPath)}
        >
          Skip
        </StyledButton>
        {generalLink && (
          <LinkToLoom
            as="a"
            target="_blank"
            color="colorized"
            href={generalLink}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.23047 1.84645L1.23047 14.1541C1.23047 14.494 1.50599 14.7695 1.84585 14.7695H14.1535C14.4934 14.7695 14.7689 14.494 14.7689 14.1541V1.84645C14.7689 1.50659 14.4934 1.23107 14.1535 1.23107H1.84585C1.50599 1.23107 1.23047 1.50659 1.23047 1.84645Z"
                stroke="#4136CF"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M6.46094 5.84766H10.1532V9.53996"
                stroke="#4136CF"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M5.8457 10.1553L10.1534 5.84766"
                stroke="#4136CF"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            More tutorials
          </LinkToLoom>
        )}
      </Buttons>
    </GuideModal>
  );
};

export default VideoGuide;
